import { PlayArrow as PlayArrowIcon, School as SchoolIcon } from "@mui/icons-material";
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { type ParticipantInfo, useAIChat, useAITutorPreferences } from "~hooks/index";

import { PageHeader } from "../../../ui/atoms/PageHeader/PageHeader";
import { RichChatMessage } from "../../atoms/Chat";

interface AIChatProps {
  userId: string;
  participantInfo: ParticipantInfo;
}

export const AIChat = ({ userId, participantInfo }: AIChatProps) => {
  const { messages, isLoading, error, sendMessage } = useAIChat({ participantInfo, userId });
  const { updatePreferences } = useAITutorPreferences();
  const [subject, setSubject] = useState(participantInfo.subject);
  const [gradeLevel, setGradeLevel] = useState(participantInfo.gradeLevel);
  const [mode, setMode] = useState(participantInfo.mode || "practice");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Update preferences when values change
  useEffect(() => {
    updatePreferences({ gradeLevel, mode, subject });
  }, [updatePreferences, gradeLevel, mode, subject]);

  const gradeOptions = [
    { label: "Kindergarten", value: "k" },
    { label: "1st Grade", value: "1" },
    { label: "2nd Grade", value: "2" },
    { label: "3rd Grade", value: "3" },
    { label: "4th Grade", value: "4" },
    { label: "5th Grade", value: "5" },
    { label: "6th Grade", value: "6" },
    { label: "7th Grade", value: "7" },
    { label: "8th Grade", value: "8" },
    { label: "9th Grade", value: "9" },
    { label: "10th Grade", value: "10" },
    { label: "11th Grade", value: "11" },
    { label: "12th Grade", value: "12" },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const subjectOptions = [
    { label: "Mathematics", value: "math" },
    { label: "Reading", value: "reading" },
    { label: "Science", value: "science" },
    { label: "Social Studies", value: "social-studies" },
    { label: "United States History", value: "us-history" },
    { label: "World History", value: "world-history" },
  ];

  const handleGenerate = async () => {
    if (isLoading) {
      return;
    }

    const gradeDisplay = gradeOptions.find((g) => g.value === gradeLevel)?.label || gradeLevel;
    const subjectDisplay = subjectOptions.find((s) => s.value === subject)?.label || subject;
    await sendMessage(`Generate ${mode} ${subjectDisplay} content for ${gradeDisplay}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 112px)", // Full height minus navbar and footer
        position: "relative",
      }}
    >
      <Box sx={{ maxWidth: { lg: "1200px", md: "1000px", sm: "800px", xs: "100%" }, mx: "auto", width: "100%" }}>
        <PageHeader title="AI Tutor" icon={<SchoolIcon data-variant="page-header" />} />
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 2,
          margin: "0 auto",
          maxWidth: { lg: "1200px", md: "1000px", sm: "800px", xs: "100%" },
          overflowY: "auto",
          p: 2,
          width: "100%",
        }}
      >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to connect to AI service. Please check your connection and try again.
          </Alert>
        )}

        {messages.length === 0 ? (
          <Box
            sx={{
              alignItems: "center",
              color: "text.secondary",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography variant="body1">Select your preferences below and click Generate to start learning!</Typography>
          </Box>
        ) : (
          <>
            {messages.map((message) => (
              <RichChatMessage
                key={message.id}
                text={message.text}
                isUser={message.isUser}
                isStreaming={isLoading && !message.isUser && message.text === ""}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Controls Area */}
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: "background.default",
          bottom: 0,
          display: "flex",
          left: 0,
          p: 3,
          position: "sticky",
          right: 0,
          width: "100%",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            maxWidth: "md",
            mx: "auto", // Center the controls horizontally
            width: "100%",
          }}
        >
          <Stack
            direction={{ sm: "row", xs: "column" }}
            spacing={3}
            alignItems={{ sm: "center", xs: "stretch" }}
            justifyContent="center"
          >
            <FormControl size="small" sx={{ minWidth: { sm: 120, xs: "100%" } }}>
              <InputLabel id="subject-label">Subject</InputLabel>
              <Select
                labelId="subject-label"
                value={subject}
                label="Subject"
                onChange={(e) => setSubject(e.target.value)}
              >
                {subjectOptions.map((subject) => (
                  <MenuItem key={subject.value} value={subject.value} title={subject.label} aria-label={subject.label}>
                    {subject.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { sm: 140, xs: "100%" } }}>
              <InputLabel id="grade-label">Grade Level</InputLabel>
              <Select
                labelId="grade-label"
                value={gradeLevel}
                label="Grade Level"
                onChange={(e) => setGradeLevel(e.target.value)}
              >
                {gradeOptions.map((grade) => (
                  <MenuItem key={grade.value} value={grade.value} title={grade.label} aria-label={grade.label}>
                    {grade.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { sm: 120, xs: "100%" } }}>
              <InputLabel id="mode-label">Mode</InputLabel>
              <Select labelId="mode-label" value={mode} label="Mode" onChange={(e) => setMode(e.target.value)}>
                <MenuItem value="practice">Practice</MenuItem>
                <MenuItem value="interactive" disabled>
                  Interactive (Coming Soon)
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              onClick={handleGenerate}
              disabled={isLoading}
              startIcon={<PlayArrowIcon />}
              sx={{
                height: 40,
                minWidth: { sm: 120, xs: "100%" },
                px: 3,
              }}
            >
              Generate
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
